import React, { useEffect, useState, useCallback } from 'react';
import { PageIndices, TabIndices } from '../common/i-doc/i-doc.registry.ts';
import { DashboardLayout } from '../components/DashboardLayout';
import { RoleProtectedRoute } from '../components/RoleProtectedRoute';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  TextField,
} from '@mui/material';

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ChatIcon from '@mui/icons-material/Chat';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventIcon from '@mui/icons-material/Event';
import { PageToolbar, FilterConfig } from '../components/common/PageToolbar';
import { UniversalPagination } from '../components/common/UniversalPagination';
import { getClients, setClientGroups } from '../services/client.service';
import { Client, ClientDetails, PaginatedClientsResponse, GetClientsParams } from '../types/client';
import { useClientOptions } from '../hooks/useClientOptions';
import { ClientDetailsModal } from '../components/clients/ClientDetailsModal';
import { toastService } from '../services/toast.service';
import { UserAvatar } from '@components/common/UserAvatar.tsx';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { acceptConnection, rejectConnection } from '../services/user-connections.service';
import { PageCard } from '@/components/common/PageCard';
import { PageHint } from '@/components/common/PageHint';
import { AdaptiveTabs } from '@/components/common/AdaptiveTabs';
import { ClientGroupsManager } from '@/components/clients/ClientGroupsManager';
import { ClientsBulkNotificationsTab } from '@/components/clients/ClientsBulkNotificationsTab';
import { ClientsQuestionnaireDistributionTab } from '@/components/clients/ClientsQuestionnaireDistributionTab';
import { ProPlanGate } from '@/components/common/ProPlanGate';
import { useExpertPlan } from '@/hooks/useExpertPlan';
import { ExpertInvitation, expertInvitationsService } from '../services/expert-invitations.service';

const CONNECTION_STATUS_CONFIG: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
  active:   { label: 'Активний',    color: 'success' },
  pending:  { label: 'Очікує',      color: 'warning' },
  rejected: { label: 'Відхилено',   color: 'error'   },
};

const INVITATION_STATUS_CONFIG: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
  pending:   { label: 'Очікує',    color: 'warning' },
  accepted:  { label: 'Прийнято', color: 'success' },
  cancelled: { label: 'Скасовано', color: 'error'  },
};

const ClientsPageContent: React.FC = () => {
  const navigate = useNavigate();
  const [searchParamsUrl] = useSearchParams();
  const { connectionStatuses, groups } = useClientOptions();
  const { hasFeature, loading: planLoading } = useExpertPlan();

  const tabParam = searchParamsUrl.get('tab');
  const activeTab = (tabParam === 'invitations' || tabParam === 'groups' || tabParam === 'bulk-notifications' || tabParam === 'questionnaire-distribution') ? tabParam : 'clients';

  const [clientsResponse, setClientsResponse] = useState<PaginatedClientsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useState<GetClientsParams>({ page: 1, limit: 10, search: '' });
  const [processingId, setProcessingId] = useState<number | null>(null);

  const [selectedClientDetails, setSelectedClientDetails] = useState<ClientDetails | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  // Invite dialog state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteSurname, setInviteSurname] = useState('');
  const [inviteEmailError, setInviteEmailError] = useState('');
  const [inviting, setInviting] = useState(false);

  // Invitations tab state
  const [invitations, setInvitations] = useState<ExpertInvitation[]>([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);

  // Group editing state
  const [editingGroupsClient, setEditingGroupsClient] = useState<Client | null>(null);
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const [isGroupsSaving, setIsGroupsSaving] = useState(false);

  const fetchClients = useCallback(async () => {
    if (activeTab !== 'clients') return;
    setLoading(true);
    setError(null);
    try {
      const response = await getClients(searchParams);
      setClientsResponse(response);
    } catch (err: any) {
      const backendMessage = err.response?.data?.message;
      const errorMessage = Array.isArray(backendMessage) ? backendMessage.join(', ') : backendMessage || 'Не вдалося завантажити список клієнтів.';
      setError(errorMessage);
      toastService.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [searchParams, activeTab]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    if (activeTab === 'invitations') fetchInvitations();
  }, [activeTab]);

  const fetchInvitations = async () => {
    setInvitationsLoading(true);
    try {
      const data = await expertInvitationsService.getInvitations();
      setInvitations(data);
    } catch {
      toastService.error('Не вдалося завантажити запрошення.');
    } finally {
      setInvitationsLoading(false);
    }
  };

  const handleOpenInvite = () => {
    setInviteEmail('');
    setInviteName('');
    setInviteSurname('');
    setInviteEmailError('');
    setInviteOpen(true);
  };

  const handleSendInvite = async () => {
    if (!inviteEmail.trim()) { setInviteEmailError('Email обов\'язковий'); return; }
    if (!/\S+@\S+\.\S+/.test(inviteEmail)) { setInviteEmailError('Невірний формат email'); return; }
    setInviting(true);
    try {
      await expertInvitationsService.sendInvitation({
        email: inviteEmail.trim(),
        name: inviteName.trim() || undefined,
        surname: inviteSurname.trim() || undefined,
      });
      toastService.success(`Запрошення надіслано на ${inviteEmail.trim()}`);
      setInviteOpen(false);
      if (activeTab === 'invitations') fetchInvitations();
    } catch (err: any) {
      const msg = err?.message || 'Не вдалося надіслати запрошення.';
      toastService.error(msg);
    } finally {
      setInviting(false);
    }
  };

  // const handleViewDetails = async (client: Client) => {
  //   try {
  //     const details = await getClientDetails(client.id);
  //     setSelectedClientDetails(details);
  //     setIsDetailsModalOpen(true);
  //   } catch (err) {
  //     toastService.error('Не вдалося завантажити детальну інформацію про клієнта.');
  //   }
  // };
  
  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setTimeout(() => {
      setSelectedClientDetails(null);
    }, 300);
  };

  const handleOpenGroupsEdit = (client: Client) => {
    setEditingGroupsClient(client);
    setSelectedGroupIds(client.groups?.map(g => g.id) || []);
  };

  const handleSaveGroups = async () => {
    if (!editingGroupsClient) return;
    setIsGroupsSaving(true);
    try {
      await setClientGroups(editingGroupsClient.id, selectedGroupIds);
      toastService.success('Групи оновлено');
      setEditingGroupsClient(null);
      fetchClients(); // Refresh list to see updated groups
    } catch (err) {
      toastService.error('Помилка при оновленні груп');
    } finally {
      setIsGroupsSaving(false);
    }
  };

  const handlePageChange = (value: number) => {
    setSearchParams(prev => ({ ...prev, page: value }));
  };

  const handleLimitChange = (value: number) => {
    setSearchParams(prev => ({ ...prev, limit: value, page: 1 }));
  };

  const handleGoToProfile = (clientId: number) => {
    navigate(`/client/${clientId}`);
  };

  const handleAccept = async (client: Client) => {
    if (!client.connectionId) return;
    setProcessingId(client.connectionId);
    try {
      await acceptConnection(client.connectionId);
      setClientsResponse(prev => prev ? {
        ...prev,
        clients: prev.clients.map(c =>
          c.id === client.id ? { ...c, connectionStatus: 'active' } : c
        ),
      } : prev);
      toastService.success('Заявку прийнято');
    } catch (err: any) {
      const msg = err.response?.data?.message;
      toastService.error(Array.isArray(msg) ? msg.join(', ') : msg || 'Помилка');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (client: Client) => {
    if (!client.connectionId) return;
    setProcessingId(client.connectionId);
    try {
      await rejectConnection(client.connectionId);
      setClientsResponse(prev => prev ? {
        ...prev,
        clients: prev.clients.map(c =>
          c.id === client.id ? { ...c, connectionStatus: 'rejected' } : c
        ),
      } : prev);
      toastService.info('Заявку відхилено');
    } catch (err: any) {
      const msg = err.response?.data?.message;
      toastService.error(Array.isArray(msg) ? msg.join(', ') : msg || 'Помилка');
    } finally {
      setProcessingId(null);
    }
  };

  const renderClientsTable = () => {
    if (loading) {
      return <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress /></Box>;
    }

    if (error && (!clientsResponse || clientsResponse.clients.length === 0)) {
        return null;
    }

    if (!clientsResponse || clientsResponse.clients.length === 0) {
      return <Typography sx={{ mt: 4, textAlign: 'center' }}>Клієнтів не знайдено.</Typography>;
    }

    return (
      <Box>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Аватар</TableCell>
                <TableCell>Ім'я / Email</TableCell>
                <TableCell>Групи</TableCell>
                <TableCell>Часовий пояс</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Коментар</TableCell>
                <TableCell align="right">Дії</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clientsResponse.clients.map(client => {
                const statusKey = client.connectionStatus ?? '';
                const statusCfg = CONNECTION_STATUS_CONFIG[statusKey];
                const isPending = statusKey === 'pending';
                const isBlocked = statusKey === 'pending' || statusKey === 'rejected';
                const isProcessing = processingId === client.connectionId;
                const clientGroupsCount = client.groups?.length || 0;
                const hasGroups = clientGroupsCount > 0;

                return (
                  <TableRow
                    hover
                    key={client.id}
                    onClick={() => !isBlocked && handleGoToProfile(client.id)}
                    sx={{ cursor: isBlocked ? 'default' : 'pointer' }}
                  >
                    <TableCell>
                      <UserAvatar path={client.avatar} name={client.name} sx={{ width: 32, height: 32 }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{client.name}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.25 }}>
                        <Typography variant="caption" color="text.secondary">{client.email}</Typography>
                        {client.isEmailVerified ? (
                          <CheckCircleIcon color="success" sx={{ ml: 0.5, fontSize: 13 }} titleAccess="Email верифіковано" />
                        ) : (
                          <CancelIcon color="action" sx={{ ml: 0.5, fontSize: 13 }} titleAccess="Email не верифіковано" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        {client.groups?.map(g => (
                          <Chip
                            key={g.id}
                            label={g.name}
                            size="small"
                            sx={{
                              bgcolor: g.color,
                              color: '#fff',
                              fontSize: '10px',
                              height: '20px'
                            }}
                          />
                        ))}
                        {!isBlocked && (
                          <Tooltip title={hasGroups ? "Редагувати групи" : "Призначити групу"}>
                            <Button
                              size="small"
                              startIcon={<LocalOfferIcon sx={{ fontSize: 16 }} />}
                              onClick={(e) => { e.stopPropagation(); handleOpenGroupsEdit(client); }}
                              sx={{ 
                                fontSize: '10px', 
                                textTransform: 'none', 
                                minWidth: 'auto',
                                py: 0,
                                px: 1,
                                ml: hasGroups ? 0.5 : 0
                              }}
                            >
                              {hasGroups ? 'Редагувати' : 'Призначити'}
                            </Button>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {client.timezone ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {statusCfg ? (
                          <Chip
                            label={statusCfg.label}
                            color={statusCfg.color}
                            size="small"
                            variant="outlined"
                          />
                        ) : statusKey ? (
                          <Chip label={statusKey} size="small" variant="outlined" />
                        ) : null}
                        {isPending && client.connectionId && (
                          <>
                            <Tooltip title="Прийняти">
                              <span>
                                <IconButton
                                  size="small"
                                  color="success"
                                  disabled={isProcessing}
                                  onClick={e => { e.stopPropagation(); handleAccept(client); }}
                                >
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Відхилити">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={isProcessing}
                                  onClick={e => { e.stopPropagation(); handleReject(client); }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {isPending && client.connectionComment ? client.connectionComment : '—'}
                    </TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()}>
                      {!isBlocked && (
                        <>
                          <Tooltip title="Чат з клієнтом">
                            <IconButton size="small" onClick={() => navigate(`/chat?clientId=${client.id}`)}>
                              <ChatIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Календар клієнта">
                            <IconButton size="small" onClick={() => navigate(`/client/${client.id}/calendar`)}>
                              <CalendarMonthIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Зустрічі з клієнтом">
                            <IconButton size="small" onClick={() => navigate(`/client/${client.id}/appointments`)}>
                              <EventIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title={
                        isPending   ? 'Профіль доступний після підтвердження заявки' :
                        statusKey === 'rejected' ? 'З\'єднання відхилено — профіль недоступний' :
                        'Перейти до профілю'
                      }>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleGoToProfile(client.id)}
                            color="primary"
                            disabled={isBlocked}
                          >
                            <AccountCircleIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <UniversalPagination
          page={clientsResponse.pagination.currentPage}
          count={clientsResponse.pagination.pages}
          limit={searchParams.limit ?? 10}
          total={clientsResponse.pagination.total}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          storageKey="pagination:clients"
        />
      </Box>
    );
  };

  const filters: FilterConfig[] = [
    {
      id: 'search',
      type: 'text',
      label: 'Пошук за іменем або email',
      value: searchParams.search ?? '',
      onChange: (value: string) => setSearchParams(prev => ({ ...prev, search: value, page: 1 })),
      flexGrow: 1,
      minWidth: '240px',
    },
    {
      id: 'groupId',
      type: 'select',
      label: 'Група',
      value: searchParams.groupId?.toString() ?? '',
      onChange: (value: string) => setSearchParams(prev => ({ ...prev, groupId: value ? Number(value) : undefined, page: 1 })),
      options: [{ value: '', label: 'Всі групи' }, ...groups.map(g => ({ value: g.id.toString(), label: g.name }))],
      minWidth: '180px',
    },
    {
      id: 'connectionStatus',
      type: 'select',
      label: 'Статус з\'єднання',
      value: searchParams.connectionStatus ?? '',
      onChange: (value: string) => setSearchParams(prev => ({ ...prev, connectionStatus: value || undefined, page: 1 })),
      options: [{ value: '', label: 'Всі статуси' }, ...connectionStatuses],
      minWidth: '180px',
    },
  ];

  return (
    <Box>
      <PageHint iDoc={PageIndices.CLIENTS} />

      <AdaptiveTabs
        tabs={[
          { value: 'clients',             label: 'Список клієнтів' },
          { value: 'invitations',         label: 'Запрошення' },
          { value: 'groups',              label: 'Налаштування груп' },
          { value: 'bulk-notifications',  label: 'Масові сповіщення', chip: 'PRO' },
          { value: 'questionnaire-distribution', label: 'Розсилка опитувань' },
        ]}
        searchParamKey="tab"
        defaultValue="clients"
      />

      <Box data-i-doc={TabIndices.CLIENTS_LIST} hidden={activeTab !== 'clients'}>
        <PageCard>
          <PageToolbar
            filters={filters}
            storageKey="filter:clients-list"
            onReset={() => setSearchParams({ page: 1, limit: 10, search: '', connectionStatus: undefined, groupId: undefined })}
            showReset
            extraActions={
              <Button variant="contained" startIcon={<PersonAddAlt1Icon />} onClick={handleOpenInvite}>
                Запросити клієнта
              </Button>
            }
          />
          {renderClientsTable()}
        </PageCard>
      </Box>

      <Box data-i-doc={TabIndices.CLIENTS_INVITATIONS} hidden={activeTab !== 'invitations'}>
        <PageCard>
          {invitationsLoading ? (
            <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress /></Box>
          ) : invitations.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary" gutterBottom>Запрошень ще немає.</Typography>
              <Button variant="contained" startIcon={<PersonAddAlt1Icon />} onClick={handleOpenInvite} sx={{ mt: 1 }}>
                Запросити клієнта
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Ім'я</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Надіслано</TableCell>
                    <TableCell>Діє до</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invitations.map(inv => {
                    const statusCfg = INVITATION_STATUS_CONFIG[inv.status] ?? { label: inv.status, color: 'default' as const };
                    const isExpired = inv.status === 'pending' && new Date() > new Date(inv.expiresAt);
                    return (
                      <TableRow key={inv.id} hover>
                        <TableCell>{inv.email}</TableCell>
                        <TableCell>{[inv.name, inv.surname].filter(Boolean).join(' ') || '—'}</TableCell>
                        <TableCell>
                          <Chip
                            label={isExpired ? 'Прострочено' : statusCfg.label}
                            color={isExpired ? 'default' : statusCfg.color}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(inv.createdAt).toLocaleDateString('uk-UA')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color={isExpired ? 'error' : 'text.secondary'}>
                            {new Date(inv.expiresAt).toLocaleDateString('uk-UA')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </PageCard>
      </Box>

      <Box data-i-doc={TabIndices.CLIENTS_GROUPS} hidden={activeTab !== 'groups'}>
        <PageCard>
          <ClientGroupsManager />
        </PageCard>
      </Box>

      <Box hidden={activeTab !== 'bulk-notifications'}>
        <PageHint iDoc={TabIndices.CLIENTS_BULK_NOTIFICATIONS} />
        <ProPlanGate hasAccess={hasFeature('bulk_notifications')} loading={planLoading}>
          <ClientsBulkNotificationsTab />
        </ProPlanGate>
      </Box>

      <Box hidden={activeTab !== 'questionnaire-distribution'}>
        <ClientsQuestionnaireDistributionTab />
      </Box>

      {/* Quick Group Selection Dialog */}
      <Dialog 
        open={!!editingGroupsClient} 
        onClose={() => setEditingGroupsClient(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Групи: {editingGroupsClient?.name}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="groups-select-label">Виберіть групи</InputLabel>
            <Select
              labelId="groups-select-label"
              multiple
              value={selectedGroupIds}
              onChange={(e) => setSelectedGroupIds(e.target.value as number[])}
              input={<OutlinedInput label="Виберіть групи" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const group = groups.find(g => g.id === value);
                    return <Chip key={value} label={group?.name} size="small" />;
                  })}
                </Box>
              )}
            >
              {groups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: group.color, mr: 1 }} />
                  {group.name}
                </MenuItem>
              ))}
              {groups.length === 0 && (
                <MenuItem disabled>Спочатку створіть групи у вкладці "Налаштування груп"</MenuItem>
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingGroupsClient(null)}>Скасувати</Button>
          <Button 
            onClick={handleSaveGroups} 
            variant="contained" 
            disabled={isGroupsSaving}
          >
            Зберегти
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite client dialog */}
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Запросити клієнта</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            required
            label="Email"
            type="email"
            value={inviteEmail}
            onChange={e => { setInviteEmail(e.target.value); setInviteEmailError(''); }}
            error={!!inviteEmailError}
            helperText={inviteEmailError}
            sx={{ mt: 1 }}
          />
          <TextField
            fullWidth
            label="Ім'я"
            value={inviteName}
            onChange={e => setInviteName(e.target.value)}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label="Прізвище"
            value={inviteSurname}
            onChange={e => setInviteSurname(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)} disabled={inviting}>Скасувати</Button>
          <Button variant="contained" onClick={handleSendInvite} disabled={inviting}>
            {inviting ? <CircularProgress size={20} /> : 'Надіслати запрошення'}
          </Button>
        </DialogActions>
      </Dialog>

      {selectedClientDetails && (
        <ClientDetailsModal
          client={selectedClientDetails}
          open={isDetailsModalOpen}
          onClose={handleCloseDetailsModal}
        />
      )}

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const ClientsPage: React.FC = () => {
  return (
    <DashboardLayout>
      <Box data-i-doc={PageIndices.CLIENTS}>
        <RoleProtectedRoute allowedRoles={['expert', 'admin', 'superadmin']}>
          <ClientsPageContent />
        </RoleProtectedRoute>
      </Box>
    </DashboardLayout>
  );
};

export default ClientsPage;
